import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Building2, Plus, Search, Download, Upload, Edit, Trash2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { plantasColumns, columnsExcel } from './utils/plantasColumns';
import { validatePlanta } from './utils/plantasValidation';
import { plantasService } from './utils/plantasService';

const PlantasPage = () => {
  const [plantas, setPlantas] = useState([]);
  const [filteredPlantas, setFilteredPlantas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCreateDisabledDialogOpen, setIsCreateDisabledDialogOpen] = useState(false);
  const [editingPlanta, setEditingPlanta] = useState(null);
  const [deletingPlanta, setDeletingPlanta] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    ciudad: '',
    direccion: '',
    telefono: '',
    encargado: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const { toast } = useToast();

  // Cargar plantas al montar el componente
  useEffect(() => {
    loadPlantas();
  }, []);

  // Filtrar plantas cuando cambie el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredPlantas(plantas);
    } else {
      const filtered = plantas.filter(planta =>
        Object.values(planta).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredPlantas(filtered);
    }
  }, [plantas, searchTerm]);

  const loadPlantas = async () => {
    try {
      setLoading(true);
      const response = await plantasService.getAll();
      // Extraer los datos de la respuesta del backend
      const data = response?.data || [];
      setPlantas(data);
    } catch (error) {
      console.error('Error loading plantas:', error);
      setPlantas([]); // Asegurar que siempre sea un array
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las plantas',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      ciudad: '',
      direccion: '',
      telefono: '',
      encargado: '',
    });
    setFormErrors({});
    setEditingPlanta(null);
  };

  const handleSubmit = async () => {
    const validation = validatePlanta(formData);
    
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    try {
      if (editingPlanta) {
        const plantaId = editingPlanta._id;
        console.log('Updating planta with ID:', plantaId, 'Data:', formData);
        await plantasService.update(plantaId, formData);
        toast({
          title: 'Éxito',
          description: 'Planta actualizada correctamente',
        });
      } else {
        await plantasService.create(formData);
        toast({
          title: 'Éxito',
          description: 'Planta creada correctamente',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadPlantas();
    } catch (error) {
      toast({
        title: 'Error',
        description: editingPlanta ? 'No se pudo actualizar la planta' : 'No se pudo crear la planta',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (planta) => {
    setEditingPlanta(planta);
    setFormData({
      nombre: planta.nombre || '',
      ciudad: planta.ciudad || '',
      direccion: planta.direccion || '',
      telefono: planta.telefono || '',
      encargado: planta.encargado || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingPlanta) return;

    try {
      await plantasService.delete(deletingPlanta._id );
      toast({
        title: 'Éxito',
        description: 'Planta eliminada correctamente',
      });
      setIsDeleteDialogOpen(false);
      setDeletingPlanta(null);
      loadPlantas();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la planta',
        variant: 'destructive',
      });
    }
  };

  const handleExport = () => {
    // Implementar exportación a Excel
    toast({
      title: 'Información',
      description: 'Función de exportación en desarrollo',
    });
  };

  const handleImport = () => {
    // Implementar importación desde Excel
    toast({
      title: 'Información',
      description: 'Función de importación en desarrollo',
    });
  };

  const handleNewPlantaClick = () => {
    setIsCreateDisabledDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Building2 className="h-8 w-8 text-primary" />
            Plantas
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestiona las plantas de la organización
          </p>
        </div>
        
        <div className="flex gap-2">


          <Button onClick={handleNewPlantaClick}>
            <Plus className="h-4 w-4 mr-2" />
            Nueva Planta
          </Button>
        </div>
      </motion.div>

      {/* Búsqueda */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar plantas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Tabla de plantas */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Lista de Plantas</CardTitle>
            <CardDescription>
              {Array.isArray(filteredPlantas) ? filteredPlantas.length : 0} planta{(Array.isArray(filteredPlantas) ? filteredPlantas.length : 0) !== 1 ? 's' : ''} encontrada{(Array.isArray(filteredPlantas) ? filteredPlantas.length : 0) !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!Array.isArray(filteredPlantas) || filteredPlantas.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm ? 'No se encontraron plantas que coincidan con la búsqueda' : 'No hay plantas registradas'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      {plantasColumns.map((column) => (
                        <th key={column.id} className="text-left p-2 font-medium">
                          {column.label}
                        </th>
                      ))}
                      <th className="text-left p-2 font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(filteredPlantas) && filteredPlantas.map((planta, index) => (
                      <motion.tr
                        key={planta._id || planta.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b hover:bg-muted/50"
                      >
                        {plantasColumns.map((column) => (
                          <td key={column.id} className="p-2">
                            {planta[column.id] || '-'}
                          </td>
                        ))}
                        <td className="p-2">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(planta)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setDeletingPlanta(planta);
                                setIsDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Dialog para crear/editar planta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingPlanta ? 'Editar Planta' : 'Nueva Planta'}
            </DialogTitle>
            <DialogDescription>
              {editingPlanta ? 'Modifica los datos de la planta' : 'Completa la información para crear una nueva planta'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {plantasColumns.map((column) => (
              <div key={column.id}>
                <Label htmlFor={column.id}>
                  {column.label} {column.required && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id={column.id}
                  value={formData[column.id]}
                  onChange={(e) => handleInputChange(column.id, e.target.value)}
                  placeholder={`Ingresa ${column.label.toLowerCase()}`}
                  className={formErrors[column.id] ? 'border-red-500' : ''}
                />
                {formErrors[column.id] && (
                  <p className="text-sm text-red-500 mt-1">{formErrors[column.id]}</p>
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit}>
              {editingPlanta ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog informativo: creación de plantas deshabilitada */}
      <Dialog open={isCreateDisabledDialogOpen} onOpenChange={setIsCreateDisabledDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Creación de plantas deshabilitada
            </DialogTitle>
            <DialogDescription>
              No es posible crear nuevas plantas desde este módulo. Si necesitas registrar una planta nueva, por favor contacta al administrador del sistema.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setIsCreateDisabledDialogOpen(false)}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Confirmar eliminación
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar la planta "{deletingPlanta?.nombre}"? 
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlantasPage;
